import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { ListInfo, ListItem } from '../types/Post';
import {
  buildListSubhead,
  formatItemCount,
  gridSquares,
  orderListItems,
  plainNote,
} from '../utils/listHelpers';
import { MediaHeader, MediaHeaderBar, MediaHeaderLeftSlot, MediaHeaderText } from './MediaHeader';
import { MediaArt } from './MediaArt';
import { usePlayback } from '../context/playback';
import { listQueueFrom } from '../utils/queueItems';
import GridSpot from './GridSpot';
import { ReactComponent as PlayIcon } from '../img/play.svg';

interface ListCarouselProps {
  list: ListInfo;
  postId: string;
  status?: string;
}

// ---------- styled ----------

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  padding: 0.5em var(--post-inset);
  background: white;
`;

// Separate rounded header above the carousel
const ListHeaderBar = styled.div`
  background: black;
  color: white;
  padding: 0.6em 1em;
  display: flex;
  align-items: center;
  border-radius: var(--radius-card);

  @media (max-width: 360px) {
    padding: 0.6em 0.6em;
  }
`;

const CardFrame = styled.div`
  position: relative;
`;

const ListHeaderText = styled.div`
  flex: 1;
`;

const ViewListButton = styled.span`
  margin-left: 0.5em;
  border: 1.5px solid white;
  border-radius: var(--radius-pill);
  padding: 0.28em 0.7em;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  color: white;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ListHeaderTitle = styled.span`
  font-weight: 900;
`;

const ListHeaderSubhead = styled.span`
  font-weight: 300;
  font-style: italic;
`;

// One slide fills the card; swipe on touch, drag with a mouse (see the
// pointer handlers), or the dots and arrow keys move between them.
const Track = styled.div<{ $dragging: boolean }>`
  cursor: ${(p) => (p.$dragging ? 'grabbing' : 'grab')};
  user-select: none;
  ${(p) => (p.$dragging ? 'scroll-snap-type: none !important; scroll-behavior: auto;' : '')}

  & img {
    -webkit-user-drag: none;
  }

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
  }

  display: flex;
  align-items: flex-start;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  gap: 8px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Slide = styled.div`
  flex: 0 0 100%;
  box-sizing: border-box;
  min-width: 0;
  scroll-snap-align: start;
  position: relative;
  border-radius: var(--radius-card);
  overflow: hidden;
  border: var(--offset-border);
  border-right-width: 2px;
  border-bottom-width: 2px;
`;

const MissingSlide = styled.div`
  aspect-ratio: 1;
  width: 100%;
  background: #222;
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
`;

const Counter = styled.div`
  position: absolute;
  bottom: 0.75em;
  right: 0.75em;
  background: rgba(0, 0, 0, 0.78);
  color: white;
  font-weight: 700;
  font-size: 0.7rem;
  padding: 0.25em 0.6em;
  border-radius: var(--radius-pill);
  z-index: 2;
  pointer-events: none;
  letter-spacing: 0.02em;
`;

// Notes are deliberately unlimited in length, so this is cropped to three
// lines here and shown in full on the list detail view. Without the clamp a
// long note grows upward until it covers the artwork it is annotating. Same
// split as the items themselves: the feed shows a preview, detail shows all.
// The gradient and padding live here; the clamp lives on the inner element.
// WebKit clips the ellipsis when a -webkit-line-clamp element carries its own
// padding-bottom, which is exactly the case on iOS, so the third line ran off
// the edge instead of ending in an ellipsis. Splitting them keeps the clamped
// box padding-free so the ellipsis has somewhere to render.
const NoteOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.6em 0.85em 0.75em;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.92) 45%, rgba(0, 0, 0, 0) 100%);
  color: white;
  font-style: italic;
  font-size: 0.85rem;
  line-height: 1.4;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  z-index: 1;
  cursor: pointer;
`;

const NoteClamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Rank chip shown in MediaHeader's leading slot on ranked lists. White-on-black
// here (the header bar is black) mirrors the black-on-white rank chip in the
// detail rows: same shape and weight, inverted to suit the surface.
const RankNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: white;
  color: black;
  font-weight: 900;
  font-size: var(--type-2);
`;

// Play button that fills MediaHeader's trailing PlaySlot (which supplies the
// white bordered circle). Transparent so only the icon shows inside the ring.
const CarouselPlayButton = styled.button`
  width: 100%;
  height: 100%;
  border: none;
  background: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

// Dots, Instagram style: at most MAX_DOTS show, in a window that slides to
// keep the current item in view, and the dots at an edge with more beyond it
// shrink. Every dot is in one strip that slides, so moving the window glides
// rather than jumps.
const MAX_DOTS = 7;
const DOT_SLOT = 12; // px: a 7px dot plus the gap

/** First dot shown, and each dot's size: 'full', or 'mid'/'small' toward an
 *  edge with more dots past it. */
export function dotWindow(count: number, active: number, max: number = MAX_DOTS) {
  const visible = Math.min(count, max);
  const start = Math.max(0, Math.min(count - visible, active - Math.floor(visible / 2)));
  const end = start + visible - 1;
  const sizes = Array.from({ length: count }, (_, i): 'full' | 'mid' | 'small' | 'hidden' => {
    if (i < start || i > end) return 'hidden';
    if (i === active) return 'full';
    const fromStart = start > 0 ? i - start : Infinity;
    const fromEnd = end < count - 1 ? end - i : Infinity;
    const edge = Math.min(fromStart, fromEnd);
    return edge === 0 ? 'small' : edge === 1 ? 'mid' : 'full';
  });
  return { start, visible, sizes };
}

const DOT_PX = { full: 7, mid: 5, small: 3, hidden: 3 };

// Vertical padding keeps each dot's ~23px tap target inside the clipping box;
// the negative margin takes it back out of the layout.
const DotsViewport = styled.div<{ $visible: number }>`
  width: ${(p) => p.$visible * DOT_SLOT}px;
  overflow: hidden;
  margin: calc(0.1em - 8px) auto -8px;
  padding: 8px 0;
`;

const DotsStrip = styled.div<{ $offset: number }>`
  display: flex;
  transform: translateX(${(p) => -p.$offset * DOT_SLOT}px);
  transition: transform 0.2s ease;
`;

const DotSlot = styled.div`
  flex: 0 0 ${DOT_SLOT}px;
  height: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// A dot outside the window takes no pointer events: its tap target would
// otherwise reach into the last visible dot's slot and win the tap.
const Dot = styled.button<{ $active: boolean; $px: number; $hidden: boolean }>`
  position: relative;
  pointer-events: ${(p) => (p.$hidden ? 'none' : 'auto')};
  width: ${(p) => (p.$active ? 9 : p.$px)}px;
  height: ${(p) => (p.$active ? 9 : p.$px)}px;
  border-radius: 50%;
  border: none;
  padding: 0;
  margin: 0;
  background: ${(p) => (p.$active ? '#000' : 'var(--gray-divider)')};
  cursor: pointer;
  transition:
    width 0.15s,
    height 0.15s,
    background 0.15s;

  /* Expand the tap target to ~23px without changing the visual dot size */
  &::after {
    content: '';
    position: absolute;
    inset: -8px;
  }

  &:hover {
    background: ${(p) => (p.$active ? '#000' : 'var(--gray-tertiary)')};
  }
`;

// ---------- grid mosaic ----------

// A 3x3's first slide: its own title bar (the list's name and View List, as
// the bar above other lists has), styled like the item slides', then the
// grid. The
// slide stretches to the tallest slide and its bar takes up the difference,
// so the grid sits flush at the bottom as the items' art does.
const GridSlide = styled(Slide)`
  align-self: stretch;
  display: flex;
  flex-direction: column;
`;

const GridSlideBar = styled(MediaHeaderBar)`
  flex: 1;
`;

// The grid itself; the slide draws its border.
const GridMosaic = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  background: #000;
`;

const GridCell = styled.div`
  aspect-ratio: 1;
  overflow: hidden;
  cursor: pointer;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const GridPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: #222;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 0.7rem;
`;

// ---------- component ----------

export default function ListCarousel(props: ListCarouselProps) {
  const { list, postId } = props;
  const navigate = useNavigate();
  const playback = usePlayback();

  // An item's play starts there and plays on through the rest of the list,
  // so the rest is loaded first (the feed carries only a preview). If that
  // load fails it plays on through what is loaded.
  //
  // The wait can be long on a slow connection, so the button shows it, and a
  // play that arrives late gives way: to a later tap here, or to anything the
  // reader started playing elsewhere in the meantime.
  const playRequestRef = useRef(0);
  const currentTrackRef = useRef(playback.currentTrack);
  currentTrackRef.current = playback.currentTrack;
  const [loadingPlayId, setLoadingPlayId] = useState<string | null>(null);
  function handlePlay(item: ListItem) {
    if (!item.media) return;
    const request = ++playRequestRef.current;
    const trackAtTap = playback.currentTrack;
    setLoadingPlayId(item.id);
    fetchFullItems().then((all) => {
      if (request !== playRequestRef.current) return;
      setLoadingPlayId(null);
      if (currentTrackRef.current !== trackAtTap) return;
      const queue = listQueueFrom(orderListItems(all), postId, item.id);
      if (queue.length > 0) playback.playNow(queue);
    });
  }

  const initialItems: ListItem[] =
    list.items && list.items.length > 0 ? list.items : list.preview || [];

  const [items, setItems] = useState<ListItem[]>(initialItems);
  const [index, setIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollIndexRef = useRef(0);

  // The feed carries only a preview of the list, so the rest is fetched once:
  // when the reader scrolls past the middle of the preview, taps a dot for an
  // item not loaded yet (the carousel then lands on that item), or plays an
  // item. One request, shared: every caller waits on the same promise.
  const fullRef = useRef<Promise<ListItem[]> | null>(null);
  // Where to land once the full list arrives. A ref, so a dot tapped while
  // the fetch is already in flight still counts, and so the fallback is the
  // index when the response comes back, not when the fetch began.
  const landOnRef = useRef<number | null>(null);
  const indexRef = useRef(0);
  indexRef.current = index;
  function fetchFullItems(): Promise<ListItem[]> {
    if (initialItems.length >= list.itemCount) return Promise.resolve(initialItems);
    if (fullRef.current) return fullRef.current;
    fullRef.current = api
      .get('/api/posts/id/' + postId)
      .then((res: any) => {
        const full: ListItem[] | undefined = res?.data?.list?.items;
        if (full && full.length > initialItems.length) {
          scrollIndexRef.current = landOnRef.current != null ? landOnRef.current : indexRef.current;
          landOnRef.current = null;
          setItems(full);
          return full;
        }
        return initialItems;
      })
      .catch((err: any) => {
        // Not kept, so a later scroll, dot or play tries again.
        fullRef.current = null;
        console.warn('ListCarousel: failed to load full items', err);
        return initialItems;
      });
    return fullRef.current;
  }
  function loadFullItems(landOn?: number) {
    if (landOn != null) landOnRef.current = landOn;
    fetchFullItems();
  }
  useEffect(() => {
    const threshold = Math.max(1, Math.floor(initialItems.length / 2));
    if (index >= threshold) loadFullItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, postId]);

  // Restore scroll position after items update
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || scrollIndexRef.current === 0) return;
    const firstSlide = el.querySelector('[data-slide]') as HTMLElement | null;
    if (!firstSlide) return;
    const w = firstSlide.offsetWidth + 8;
    el.scrollLeft = w * scrollIndexRef.current;
  }, [items.length]);

  function handleScroll() {
    const el = trackRef.current;
    if (!el) return;
    const firstSlide = el.querySelector('[data-slide]') as HTMLElement | null;
    const w = firstSlide ? firstSlide.offsetWidth + 8 : el.clientWidth;
    const newIndex = Math.round(el.scrollLeft / w);
    if (newIndex !== index) setIndex(newIndex);
  }

  function scrollToIndex(i: number) {
    const el = trackRef.current;
    if (!el) return;
    const firstSlide = el.querySelector('[data-slide]') as HTMLElement | null;
    const w = firstSlide ? firstSlide.offsetWidth + 8 : el.clientWidth;
    el.scrollTo({ left: w * i, behavior: 'smooth' });
  }

  // Mouse drag. Touch swipes scroll natively; a mouse cannot drag an overflow
  // container, so the track follows the pointer and, on release, settles on
  // the next or previous slide past a short pull, else back where it was.
  // Snapping is off mid-drag or it fights the pointer. A drag is never a tap:
  // the click that ends it is swallowed so it does not open the list or play.
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ x: number; scroll: number; start: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const el = trackRef.current;
    if (!el) return;
    dragRef.current = { x: e.clientX, scroll: el.scrollLeft, start: index, moved: false };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    const el = trackRef.current;
    if (!drag || !el) return;
    const dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 5) return;
    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
      el.setPointerCapture(e.pointerId);
    }
    el.scrollLeft = drag.scroll - dx;
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || !drag.moved) return;
    suppressClickRef.current = true;
    setDragging(false);
    const dx = e.clientX - drag.x;
    let target = drag.start;
    if (dx <= -40) target = drag.start + 1;
    else if (dx >= 40) target = drag.start - 1;
    target = Math.max(0, Math.min(totalSlides - 1, target));
    // After the snap style is back, or the browser snaps first and jumps.
    requestAnimationFrame(() => scrollToIndex(target));
  }

  function onClickCapture(e: React.MouseEvent) {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowRight' && index < totalSlides - 1) {
      e.preventDefault();
      scrollToIndex(index + 1);
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      scrollToIndex(index - 1);
    }
  }

  const displayItems = orderListItems(items);
  const isRanking = list.orderStyle === 'ranking';
  const isGrid = list.orderStyle === 'grid';
  // A 3x3's carousel opens on its grid: one slide ahead of the items, so
  // item i is slide i + lead.
  const lead = isGrid ? 1 : 0;
  // An item's slide, and a 3x3 item's square (its position; a draft can
  // leave squares empty, so it is not always its place in the list).
  const slideOf = (item: ListItem) => displayItems.indexOf(item) + lead;
  const squareOf = (item: ListItem, i: number) =>
    typeof item.position === 'number' && item.position >= 0 && item.position < 9
      ? item.position
      : i;
  const totalSlides = displayItems.length + lead;

  // Counter: "1 / 5", counting items; the grid's slide has none.
  const counterText =
    totalSlides > 1 && index >= lead ? index - lead + 1 + ' / ' + list.itemCount : null;

  // Dots: one per item, decided by the list's size, not by how many items
  // have loaded: a 4-item preview of an 11-item list used to show four dots
  // that vanished once the full list arrived. Past MAX_DOTS they window.
  const showDots = list.itemCount + lead > 1;
  const dots = dotWindow(list.itemCount + lead, index);

  // A focused dot that slides out of the window (the track was swiped, not
  // the dot pressed) hands focus to the current dot rather than keeping it
  // on a hidden one.
  const dotsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = dotsRef.current;
    const focused = document.activeElement as HTMLElement | null;
    if (!strip || !focused || !strip.contains(focused)) return;
    if (focused.getAttribute('aria-hidden') !== 'true') return;
    const current = strip.querySelector('[data-dot="' + index + '"]') as HTMLElement | null;
    if (current) current.focus({ preventScroll: true });
  }, [index]);

  return (
    <Wrapper>
      {/* A 3x3 names itself on its grid slide instead. */}
      {!isGrid && (
        <ListHeaderBar
          onClick={function (e: React.MouseEvent) {
            e.stopPropagation();
            navigate('/post/' + postId);
          }}
          style={{ cursor: 'pointer' }}
        >
          <ListHeaderText>
            <ListHeaderTitle>{list.title}</ListHeaderTitle>
            <br />
            <ListHeaderSubhead>{buildListSubhead(list)}</ListHeaderSubhead>
          </ListHeaderText>
          <ViewListButton
            aria-label={props.status === 'unpublished' ? 'Edit list' : 'View full list'}
          >
            {props.status === 'unpublished' ? 'Edit List' : 'View List'}
          </ViewListButton>
        </ListHeaderBar>
      )}

      {
        <>
          <CardFrame>
            {counterText && <Counter>{counterText}</Counter>}

            <Track
              ref={trackRef}
              onScroll={handleScroll}
              $dragging={dragging}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onClickCapture={onClickCapture}
              onKeyDown={onKeyDown}
              tabIndex={0}
              aria-label={list.title}
            >
              {/* A 3x3's first slide is its grid; a square jumps to its item. */}
              {isGrid && (
                <GridSlide key="grid" data-slide>
                  {/* The whole bar opens the list, as the title bar above
                      other lists does. */}
                  <GridSlideBar
                    role="link"
                    tabIndex={0}
                    onKeyDown={function (e: React.KeyboardEvent) {
                      if (e.key !== 'Enter') return;
                      e.stopPropagation();
                      navigate('/post/' + postId);
                    }}
                    onClick={function (e: React.MouseEvent) {
                      e.stopPropagation();
                      navigate('/post/' + postId);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <MediaHeaderLeftSlot>
                      <GridSpot index={-1} all onDark size={26} />
                    </MediaHeaderLeftSlot>
                    <MediaHeaderText>
                      <ListHeaderTitle>{list.title}</ListHeaderTitle>
                      <br />
                      <ListHeaderSubhead>{buildListSubhead(list)}</ListHeaderSubhead>
                    </MediaHeaderText>
                    <ViewListButton
                      aria-label={props.status === 'unpublished' ? 'Edit list' : 'View full list'}
                    >
                      {props.status === 'unpublished' ? 'Edit List' : 'View List'}
                    </ViewListButton>
                  </GridSlideBar>
                  <GridMosaic
                    // A group, not an image: its squares are buttons.
                    role="group"
                    aria-label={
                      '3x3 grid for "' +
                      list.title +
                      '". ' +
                      formatItemCount(list.itemCount, list.kind) +
                      '.'
                    }
                  >
                    {gridSquares(displayItems).map((item, i) =>
                      // An empty square of a draft: drawn, not a button.
                      !item ? (
                        <GridCell key={'empty-' + i} aria-hidden="true">
                          <GridPlaceholder />
                        </GridCell>
                      ) : (
                        <GridCell
                          key={item.id || i}
                          role="button"
                          tabIndex={0}
                          aria-label={'Show item ' + (i + 1)}
                          onClick={function (e: React.MouseEvent) {
                            e.stopPropagation();
                            scrollToIndex(slideOf(item));
                          }}
                          onKeyDown={function (e: React.KeyboardEvent) {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            e.stopPropagation();
                            scrollToIndex(slideOf(item));
                            // Focus goes with it: left on the square, it would
                            // sit off screen, and the next Tab would scroll back
                            // to the grid.
                            const slide =
                              trackRef.current?.querySelectorAll<HTMLElement>('[data-slide]')[
                                slideOf(item)
                              ];
                            if (slide) slide.focus({ preventScroll: true });
                          }}
                        >
                          {item.media && item.media.artwork_url ? (
                            <img
                              src={item.media.artwork_url
                                .replace('{w}x{h}', '300x300')
                                .replace('{w}', '300')
                                .replace('{h}', '300')}
                              alt={
                                (item.media.song_title || item.media.album_title || '') +
                                ' by ' +
                                item.media.artist
                              }
                              loading="lazy"
                            />
                          ) : (
                            <GridPlaceholder>?</GridPlaceholder>
                          )}
                        </GridCell>
                      )
                    )}
                  </GridMosaic>
                </GridSlide>
              )}
              {displayItems.map((item, i) => (
                <Slide key={item.id || i} data-slide tabIndex={-1}>
                  {item.media ? (
                    <>
                      <MediaHeader
                        media={item.media}
                        leftSlot={
                          isGrid ? (
                            <GridSpot index={squareOf(item, i)} onDark size={26} />
                          ) : isRanking ? (
                            <RankNumber>{list.itemCount - i}</RankNumber>
                          ) : undefined
                        }
                        playButton={
                          <CarouselPlayButton
                            onClick={function (e: React.MouseEvent) {
                              e.stopPropagation();
                              handlePlay(item);
                            }}
                            aria-label="Play"
                            aria-busy={loadingPlayId === item.id}
                          >
                            {loadingPlayId === item.id ? (
                              '\u22EF'
                            ) : (
                              <PlayIcon width="14" height="14" style={{ color: 'white' }} />
                            )}
                          </CarouselPlayButton>
                        }
                      />
                      <MediaArt media={item.media} size="600x600" />
                      {item.note ? (
                        <NoteOverlay
                          role="button"
                          tabIndex={0}
                          aria-label="Read the full note on the list page"
                          onClick={function (e: any) {
                            e.stopPropagation();
                            navigate('/post/' + postId);
                          }}
                          onKeyDown={function (e: any) {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate('/post/' + postId);
                            }
                          }}
                        >
                          <NoteClamp>{plainNote(item.note)}</NoteClamp>
                        </NoteOverlay>
                      ) : null}
                    </>
                  ) : (
                    <MissingSlide>Unavailable</MissingSlide>
                  )}
                </Slide>
              ))}
            </Track>
          </CardFrame>

          {showDots && (
            <DotsViewport $visible={dots.visible}>
              <DotsStrip $offset={dots.start} ref={dotsRef}>
                {dots.sizes.map((size, i) => (
                  <DotSlot key={i}>
                    <Dot
                      $active={i === index}
                      $px={DOT_PX[size]}
                      $hidden={size === 'hidden'}
                      data-dot={i}
                      aria-label={i < lead ? 'Grid' : 'Item ' + (i - lead + 1)}
                      aria-hidden={size === 'hidden' ? true : undefined}
                      tabIndex={size === 'hidden' ? -1 : undefined}
                      onClick={() => (i < totalSlides ? scrollToIndex(i) : loadFullItems(i))}
                    />
                  </DotSlot>
                ))}
              </DotsStrip>
            </DotsViewport>
          )}
        </>
      }
    </Wrapper>
  );
}
